// Analytics and marketing tags, gated by the visitor's cookie consent.
//
// - Google Tag Manager (GTM-WKB2CJVF) is loaded once by initTracking(). Google
//   consent mode defaults are pushed first ("denied"), so GTM only measures
//   once the visitor has chosen. A stored choice is applied straight away.
// - The choice lives in the cookie tp_cookie_consent_v1 (same format as the
//   old site, shared with timepeak.nl): {necessary, analytics, marketing,
//   timestamp, version: 1}, 365 days.
// - The Meta Pixel is only loaded with marketing consent.
// - track(event, params) pushes to the dataLayer; window.timepeakTrack is the
//   same function for older code.
//
// On local development hosts the dataLayer is filled but no third-party
// script is loaded, so testing never pollutes the real statistics.

export interface ConsentState {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
  version: 1;
}

type Params = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    timepeakTrack?: (event: string, params?: Params) => void;
    fbq?: ((...args: unknown[]) => void) & { callMethod?: (...args: unknown[]) => void; queue?: unknown[]; loaded?: boolean; version?: string; push?: unknown };
    _fbq?: unknown;
  }
}

const GTM_ID = 'GTM-WKB2CJVF';
const META_PIXEL_ID = '926194476489874';
const CONSENT_COOKIE = 'tp_cookie_consent_v1';
const CONSENT_VERSION = 1;
const CONSENT_MAX_AGE_S = 365 * 24 * 60 * 60;

let initialised = false;
let metaLoaded = false;

function isBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

/** Local development / test hosts: no third-party scripts. */
function isLocalHost(): boolean {
  const host = location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host.endsWith('.localhost') || host.endsWith('.test') || host.endsWith('.local');
}

function dataLayer(): unknown[] {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

// Consent mode commands must be pushed as an Arguments object (gtag signature).
function gtag(..._args: unknown[]) {
  // eslint-disable-next-line prefer-rest-params
  dataLayer().push(arguments);
}

// ---------------------------------------------------------------- cookie

function readCookie(name: string): string | null {
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

/** The stored choice, or null when the visitor has not chosen (or it expired). */
export function readConsent(): ConsentState | null {
  if (!isBrowser()) return null;
  const raw = readCookie(CONSENT_COOKIE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ConsentState> | null;
    if (!parsed || parsed.version !== CONSENT_VERSION || typeof parsed.timestamp !== 'number') return null;
    if (Date.now() - parsed.timestamp > CONSENT_MAX_AGE_S * 1000) return null;
    return { necessary: true, analytics: !!parsed.analytics, marketing: !!parsed.marketing, timestamp: parsed.timestamp, version: 1 };
  } catch {
    return null;
  }
}

function writeConsent(value: ConsentState) {
  let cookie = `${CONSENT_COOKIE}=${encodeURIComponent(JSON.stringify(value))}; path=/; max-age=${CONSENT_MAX_AGE_S}; SameSite=Lax`;
  if (location.protocol === 'https:') cookie += '; Secure';
  if (/(^|\.)timepeak\.nl$/i.test(location.hostname)) cookie += '; domain=.timepeak.nl';
  document.cookie = cookie;
}

// ---------------------------------------------------------------- consent

function applyConsent(consent: Pick<ConsentState, 'analytics' | 'marketing'>) {
  const analytics = consent.analytics ? 'granted' : 'denied';
  const marketing = consent.marketing ? 'granted' : 'denied';
  gtag('consent', 'update', {
    analytics_storage: analytics,
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing
  });
  dataLayer().push({ event: 'consent_update', analytics_consent: analytics, marketing_consent: marketing });
  if (consent.marketing) loadMetaPixel();
  else if (typeof window.fbq === 'function') window.fbq('consent', 'revoke');
}

/** Store the visitor's choice and apply it to the tags. */
export function saveConsent(choice: { analytics: boolean; marketing: boolean }): ConsentState {
  const value: ConsentState = { necessary: true, analytics: !!choice.analytics, marketing: !!choice.marketing, timestamp: Date.now(), version: 1 };
  if (isBrowser()) {
    writeConsent(value);
    initTracking();
    applyConsent(value);
  }
  return value;
}

function hasMarketingConsent(): boolean {
  return !!readConsent()?.marketing;
}

// ---------------------------------------------------------------- loaders

function loadScript(src: string) {
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

function loadMetaPixel() {
  if (metaLoaded || !isBrowser()) return;
  metaLoaded = true;
  if (!window.fbq) {
    const fbq = function (...args: unknown[]) {
      if (fbq.callMethod) fbq.callMethod.apply(fbq, args);
      else fbq.queue!.push(args);
    } as NonNullable<Window['fbq']>;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    window.fbq = fbq;
    if (!window._fbq) window._fbq = fbq;
    if (!isLocalHost()) loadScript('https://connect.facebook.net/en_US/fbevents.js');
  }
  window.fbq!('consent', 'grant');
  window.fbq!('init', META_PIXEL_ID);
  window.fbq!('track', 'PageView');
}

/**
 * Load Google Tag Manager once, with consent defaults "denied" until the
 * visitor chooses. Safe to call from every page; later calls do nothing.
 */
export function initTracking(): void {
  if (initialised || !isBrowser()) return;
  initialised = true;
  window.timepeakTrack = track;

  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500
  });
  const stored = readConsent();
  if (stored) applyConsent(stored);

  dataLayer().push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  if (!isLocalHost()) loadScript(`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`);
}

// ---------------------------------------------------------------- events

/** Push an event to the dataLayer (GTM decides, per consent, what is sent). */
export function track(event: string, params?: Params): void {
  if (!isBrowser()) return;
  try {
    const payload: Params = { event };
    if (params) for (const [key, value] of Object.entries(params)) if (value !== undefined) payload[key] = value;
    dataLayer().push(payload);
  } catch { /* never break the page for analytics */ }
}

/** Account created: GTM event (+ Meta CompleteRegistration with marketing consent). */
export function trackSignUp(method: 'email' | 'google', user: { email?: string | null; name?: string | null } = {}): void {
  if (!isBrowser()) return;
  const eventId = `reg_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  const full = (user.name || '').trim();
  const [first = '', ...rest] = full.split(/\s+/);
  track('sign_up_completed', {
    event_id: eventId,
    registration_method: method,
    user_email: user.email || undefined,
    user_first_name: first || undefined,
    user_last_name: rest.join(' ') || undefined
  });
  if (hasMarketingConsent() && typeof window.fbq === 'function') {
    window.fbq('track', 'CompleteRegistration', { content_name: 'Timepeak account', currency: 'EUR', value: 0 }, { eventID: eventId });
  }
  try {
    sessionStorage.setItem('tp_last_reg_event_id', eventId);
  } catch { /* ignore */ }
}
